import { Text, View } from 'react-native';
import { Badge } from './Badge';
import { Button } from './Button';
import { Card } from './Card';
import { formatCurrency, formatRelativeTime, toTitleCase } from '../lib/utils';
import { webPrivacyProps } from '../lib/privacy';

type AssetSummaryCardProps = {
  asset: {
    id: string;
    name: string;
    assetType: 'home' | 'vehicle' | 'rental_property' | 'other';
    description: string | null;
    address: {
      street: string | null;
      city: string | null;
      region: string | null;
      postalCode: string | null;
      country: string | null;
    } | null;
    vehicle: {
      vin: string | null;
      mileage: number | null;
      year: number | null;
      make: string | null;
      model: string | null;
      trim: string | null;
    } | null;
    estimatedValue: number | null;
    annualGrowthRate: number;
    valuationSource: 'manual' | 'redfin' | 'zillow' | 'marketcheck' | 'nhtsa' | null;
    valuationConfidence: number | null;
    valuationUpdatedAt: Date | string | null;
  };
  onDelete?: (id: string) => void;
  deleting?: boolean;
};

function formatAddress(address: AssetSummaryCardProps['asset']['address']): string | null {
  if (!address) {
    return null;
  }

  return [address.street, address.city, address.region, address.postalCode]
    .filter(Boolean)
    .join(', ');
}

function getBadgeVariant(
  assetType: AssetSummaryCardProps['asset']['assetType'],
): 'blue' | 'yellow' | 'green' | 'gray' {
  switch (assetType) {
    case 'home':
      return 'blue';
    case 'vehicle':
      return 'yellow';
    case 'rental_property':
      return 'green';
    default:
      return 'gray';
  }
}

export function AssetSummaryCard({
  asset,
  onDelete,
  deleting = false,
}: AssetSummaryCardProps) {
  const address = formatAddress(asset.address);
  const valuationLabel = asset.valuationSource ? toTitleCase(asset.valuationSource) : 'No estimate';
  const growthLabel = asset.annualGrowthRate >= 0 ? 'Growth' : 'Depreciation';

  return (
    <Card>
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1">
          <Text
            className="text-base font-semibold text-gray-900 dark:text-gray-100"
            {...webPrivacyProps('private')}
          >
            {asset.name}
          </Text>
          <Text className="text-xs text-gray-500 mt-1" {...webPrivacyProps('private')}>
            {asset.description ?? address ?? asset.vehicle?.vin ?? 'Major asset'}
          </Text>
        </View>
        <Badge label={toTitleCase(asset.assetType)} variant={getBadgeVariant(asset.assetType)} />
      </View>

      <View className="mt-4 flex-row justify-between gap-3">
        <View>
          <Text className="text-xs uppercase tracking-wide text-gray-400">Value</Text>
          <Text
            className="text-lg font-bold text-gray-900 dark:text-gray-100"
            {...webPrivacyProps('financial')}
          >
            {asset.estimatedValue != null
              ? formatCurrency(asset.estimatedValue, { decimals: 0 })
              : 'Missing'}
          </Text>
          <Text className="text-xs text-gray-500 mt-1">
            {valuationLabel}
            {asset.valuationUpdatedAt ? ` · ${formatRelativeTime(asset.valuationUpdatedAt)}` : ''}
          </Text>
          <Text className="text-xs text-gray-500 mt-1">
            {growthLabel} {`${(Math.abs(asset.annualGrowthRate) * 100).toFixed(1)}%`} / yr
          </Text>
        </View>

      </View>

      {asset.vehicle && (
        <Text className="text-xs text-gray-500 mt-3" {...webPrivacyProps('private')}>
          {[
            asset.vehicle.year,
            asset.vehicle.make,
            asset.vehicle.model,
            asset.vehicle.trim,
          ]
            .filter(Boolean)
            .join(' ')}
          {asset.vehicle.mileage != null ? ` · ${asset.vehicle.mileage.toLocaleString()} miles` : ''}
        </Text>
      )}

      {onDelete && (
        <View className="mt-4">
          <Button variant="secondary" onPress={() => onDelete(asset.id)} loading={deleting}>
            Remove
          </Button>
        </View>
      )}
    </Card>
  );
}
